import { defineStore } from 'pinia'
import { ref } from 'vue'
import tokenService from '../utils/tokenService.js'
import { useAuthStore } from './auth.js'

const TTL_TAREAS = 60 * 60 * 1000      // 1 hora
const TTL_PROYECTOS = 15 * 60 * 1000   // 15 minutos

export const useCatalogStore = defineStore('catalog', () => {
  // --- State ---
  const tareas = ref([])
  const proyectos = ref([])
  const userConfig = ref(null)

  const lastFetch = ref({
    tareas: null,
    proyectos: null,
    userConfig: null
  })

  // Promesas en vuelo para evitar fetches duplicados
  const pending = {
    tareas: null,
    proyectos: null,
    userConfig: null
  }

  // --- Helpers ---

  function isStale(key, ttl) {
    const fetched = lastFetch.value[key]
    if (!fetched) return true
    if (!ttl) return false // sin TTL = nunca stale (solo invalidación manual)
    return Date.now() - fetched > ttl
  }

  // --- Tareas ---
  // GET Tarea → ~215 objetos {ta_Id, ta_Nombre, ta_Property1, ta_Property2, ...}
  // TTL: 1 hora. Se invalida manualmente si admin crea/edita tarea.

  async function ensureTareas(force = false) {
    if (!force && tareas.value.length > 0 && !isStale('tareas', TTL_TAREAS)) {
      return tareas.value
    }

    // Reusar promesa en vuelo si ya hay un fetch corriendo
    if (pending.tareas) return pending.tareas

    pending.tareas = (async () => {
      try {
        const result = await tokenService.get('Tarea')
        tareas.value = result.data || result
        lastFetch.value.tareas = Date.now()
        return tareas.value
      } finally {
        pending.tareas = null
      }
    })()

    return pending.tareas
  }

  function invalidateTareas() {
    lastFetch.value.tareas = null
  }

  // Buscar tarea por ID en cache (reemplaza GET Tarea/{id}).
  // También resuelve los IDs sintéticos de D2 ("pi_<id>" ítem de presupuesto,
  // "tn_<id>" tarea no presupuestada) contra imputablesLookup, para que el
  // resto de la app (nombres en pantalla, etc.) no tenga que distinguir el
  // origen del ID.
  function getTareaById(tareaId) {
    if (typeof tareaId === 'string' && (tareaId.startsWith('pi_') || tareaId.startsWith('tn_'))) {
      return imputablesLookup.value.get(tareaId) || null
    }
    return tareas.value.find(t => t.ta_Id === tareaId) || null
  }

  // --- Imputables de presupuesto (D2 - Timesheet por tarea) ---
  // GET PresupuestoItem/proyecto/{id}/imputables → { tieneVigente, items, tareasNoPresupuestadas }
  // Cacheado por proyecto; TTL corto porque el presupuesto puede cambiar durante el día.
  const TTL_IMPUTABLES = 5 * 60 * 1000 // 5 minutos

  const imputablesPorProyecto = ref(new Map()) // projectId -> { tieneVigente, items, tareasNoPresupuestadas, fetchedAt }
  const imputablesLookup = ref(new Map())      // "pi_<id>" | "tn_<id>" -> { ta_Id, ta_Nombre, ta_Property1, ta_Property2 }
  const pendingImputables = new Map()          // projectId -> Promise

  async function ensureImputables(projectId, force = false) {
    if (!projectId) return { tieneVigente: false, items: [], tareasNoPresupuestadas: [] }

    const cached = imputablesPorProyecto.value.get(projectId)
    if (!force && cached && Date.now() - cached.fetchedAt < TTL_IMPUTABLES) {
      return cached
    }

    if (pendingImputables.has(projectId)) return pendingImputables.get(projectId)

    const promise = (async () => {
      try {
        const authStore = useAuthStore()
        // El backend usa esto para decidir si "Administration Support" entra
        // en tareasNoPresupuestadas - queda reservada a personal administrativo.
        const result = await tokenService.get(`PresupuestoItem/proyecto/${projectId}/imputables?userId=${authStore.userId}`)
        const data = result?.data || { tieneVigente: false, items: [], tareasNoPresupuestadas: [] }
        const entry = { ...data, fetchedAt: Date.now() }
        imputablesPorProyecto.value.set(projectId, entry)

        for (const item of data.items || []) {
          imputablesLookup.value.set(`pi_${item.id}`, {
            ta_Id: `pi_${item.id}`,
            ta_Nombre: `${item.numero} - ${item.denominacion}`,
            ta_Property1: 0,
            ta_Property2: 0
          })
        }
        for (const t of data.tareasNoPresupuestadas || []) {
          imputablesLookup.value.set(`tn_${t.id}`, {
            ta_Id: `tn_${t.id}`,
            ta_Nombre: t.denominacion,
            ta_Property1: 0,
            ta_Property2: 0,
            tipoImputacion: t.tipo // 'CAPACITACION' | 'ADMIN_SUPPORT', ver GETPresupuestoItemImputables.mjs
          })
        }

        return entry
      } finally {
        pendingImputables.delete(projectId)
      }
    })()

    pendingImputables.set(projectId, promise)
    return promise
  }

  // Lista de opciones lista para pasarle a TareaPicker (extraItems), ya
  // formateada en la forma {ta_Id, ta_Nombre} - ver spec §6.2.2.
  function getImputablesOptions(projectId) {
    const cached = imputablesPorProyecto.value.get(projectId)
    if (!cached) return { tieneVigente: false, options: [] }

    const itemOptions = (cached.items || []).map((i) => imputablesLookup.value.get(`pi_${i.id}`))
    const catalogoOptions = (cached.tareasNoPresupuestadas || []).map((t) => imputablesLookup.value.get(`tn_${t.id}`))

    return { tieneVigente: cached.tieneVigente, options: [...itemOptions, ...catalogoOptions] }
  }

  // Decodifica un tareaId (posiblemente sintético) a los campos que espera
  // el backend en POST/PUT Times - ver POSTTimes.mjs / update/PUTupdate.mjs.
  function decodeTareaId(tareaId) {
    if (typeof tareaId === 'string' && tareaId.startsWith('pi_')) {
      return { tareaid: null, tipoImputacion: 'ITEM_PRESUPUESTO', presupuestoItemId: Number(tareaId.slice(3)), tareaNoPresupuestadaId: null }
    }
    if (typeof tareaId === 'string' && tareaId.startsWith('tn_')) {
      const tn = imputablesLookup.value.get(tareaId)
      return { tareaid: null, tipoImputacion: tn?.tipoImputacion || 'CAPACITACION', presupuestoItemId: null, tareaNoPresupuestadaId: Number(tareaId.slice(3)) }
    }
    return { tareaid: tareaId, tipoImputacion: null, presupuestoItemId: null, tareaNoPresupuestadaId: null }
  }

  // Inversa de decodeTareaId: reconstruye el ID sintético a partir de una fila
  // ya guardada (viene de v_TimeSheet vía GETTimes.mjs, con las columnas
  // presupuestoItemId/tareaNoPresupuestadaId/tipoImputacion agregadas para
  // esto). Sin esto, una hora cargada contra un ítem de presupuesto vuelve
  // del servidor con tareaId null y no matchea ninguna opción del picker -
  // se ve como si no se hubiera guardado, aunque sí se guardó (ver
  // GETReportePresupuestoEjecutado, que lee las tablas directo y no tiene
  // este problema).
  function encodeTareaId(row) {
    if (row.tipoImputacion === 'ITEM_PRESUPUESTO' && row.presupuestoItemId != null) {
      return `pi_${row.presupuestoItemId}`
    }
    if (row.tipoImputacion && row.tareaNoPresupuestadaId != null) {
      return `tn_${row.tareaNoPresupuestadaId}`
    }
    return row.tareaId
  }

  // --- Proyectos ---
  // GET Proyecto?abierto=1 → proyectos abiertos con {prID, tipo, text, ...}
  // TTL: 15 minutos.

  async function ensureProyectos(force = false) {
    if (!force && proyectos.value.length > 0 && !isStale('proyectos', TTL_PROYECTOS)) {
      return proyectos.value
    }

    if (pending.proyectos) return pending.proyectos

    pending.proyectos = (async () => {
      try {
        const result = await tokenService.get('Proyecto?abierto=1')
        const data = result.data || result
        // Pre-mapear ProjectIndex para que las vistas no repitan este cálculo
        proyectos.value = data.map(p => ({
          ...p,
          ProjectIndex: p.prID + p.tipo
        }))
        lastFetch.value.proyectos = Date.now()
        return proyectos.value
      } finally {
        pending.proyectos = null
      }
    })()

    return pending.proyectos
  }

  function invalidateProyectos() {
    lastFetch.value.proyectos = null
  }

  // --- User Config ---
  // GET User/{userId} → config + perfil del usuario
  // Sin TTL: vive toda la sesión. Se invalida al guardar en ConfigView.

  async function ensureUserConfig(force = false) {
    const authStore = useAuthStore()

    if (!force && userConfig.value && !isStale('userConfig', null)) {
      return userConfig.value
    }

    if (pending.userConfig) return pending.userConfig

    pending.userConfig = (async () => {
      try {
        const result = await tokenService.get('User/' + authStore.userId)
        const data = result.data || result
        // El endpoint puede devolver array o objeto
        userConfig.value = Array.isArray(data) && data.length > 0 ? data[0] : data
        lastFetch.value.userConfig = Date.now()

        // Sincronizar preferencias al authStore
        if (userConfig.value) {
          authStore.setTimeTipo(userConfig.value.us_ts_TimeTipo)
          authStore.setRowMode(userConfig.value.us_ts_RowMode)
          authStore.setConfirmDelete(userConfig.value.us_ts_ConfirmDelete)
          authStore.setTimeInterval(userConfig.value.us_ts_TimeInterval)
        }

        return userConfig.value
      } finally {
        pending.userConfig = null
      }
    })()

    return pending.userConfig
  }

  function invalidateUserConfig() {
    lastFetch.value.userConfig = null
  }

  // --- Donut (cálculo local) ---
  // Reemplaza GET Donuts?fecha=X&user=Y
  // Calcula desde timesStore en vez de ir al backend.
  // Nota: requiere que timesStore ya tenga los datos del día cacheados (ensureRange previo).

  function computeDonutData(timesStore, fecha) {
    const dayData = timesStore.getDayData(fecha, 1)

    const donutData = dayData.map((row, index) => ({
      id: row.ts_ProjectId + '_' + row.ts_Tipo,
      project: row.desc,
      time: timeToMinutes(row.tiempo),
      orden: index + 1
    }))

    const total = donutData.reduce((sum, r) => sum + r.time, 0)
    donutData.push({
      id: 'libre',
      project: 'Libre',
      time: Math.max(0, 720 - total),
      orden: 0
    })

    return donutData
  }

  function timeToMinutes(time) {
    if (!time) return 0
    const parts = time.split(':')
    return Number(parts[0]) * 60 + Number(parts[1])
  }

  // --- Limpieza (logout) ---

  function clearAll() {
    tareas.value = []
    proyectos.value = []
    userConfig.value = null
    imputablesPorProyecto.value = new Map()
    imputablesLookup.value = new Map()
    lastFetch.value = { tareas: null, proyectos: null, userConfig: null }
    pending.tareas = null
    pending.proyectos = null
    pending.userConfig = null
    pendingImputables.clear()
  }

  return {
    // State (readonly desde fuera, se modifica via actions)
    tareas,
    proyectos,
    userConfig,
    imputablesPorProyecto,

    // Actions: carga con cache
    ensureTareas,
    ensureProyectos,
    ensureUserConfig,
    ensureImputables,

    // Actions: invalidación
    invalidateTareas,
    invalidateProyectos,
    invalidateUserConfig,

    // Helpers
    getTareaById,
    computeDonutData,
    getImputablesOptions,
    decodeTareaId,
    encodeTareaId,

    // Limpieza
    clearAll
  }
})
